import React, { useRef, useEffect } from 'react';
import { Check, AlertTriangle, Sparkles } from 'lucide-react';
import { MarkdownWithCitations } from '../citations/MarkdownWithCitations';
import { fromAskSource, fromAskSourceWithExcerpt } from '../../types/citation';
import { AgentToolCallIndicator } from '../agent/AgentToolCallIndicator';
import { OUTPUT_WIDGET_REGISTRY } from '../agent/widgets';
import { OutputWidgetBlock } from '../agent/OutputWidgetBlock';
import { ThinkingIndicator } from './ThinkingIndicator';
import type { ChatMessage, AskSource, AgentToolCall } from '../../contexts/AskSidebarContext';

const TOOL_STATUS_LABELS: Record<string, string> = {
  search_documents: 'Searching records',
  list_documents: 'Indexing records',
  read_document: 'Reading document',
  get_chronology: 'Reviewing timeline',
  list_analyses: 'Reviewing analyses',
  read_analysis: 'Examining analysis',
  get_case_overview: 'Assessing case',
  trigger_analysis: 'Running analysis',
  search_guidelines: 'Consulting guidelines',
  search_pubmed: 'Searching literature',
  lookup_abbreviation: 'Looking up abbreviation',
  lookup_limitation_period: 'Checking limitation period',
  search_case_law: 'Searching case law',
  check_drug_interactions: 'Checking drug interactions',
  present_data: 'Preparing visual',
  get_metadata: 'Reviewing case profile',
};

function getThinkingStatus(toolCalls?: AgentToolCall[]): string {
  if (!toolCalls || toolCalls.length === 0) return 'Thinking';
  const running = toolCalls.filter(tc => tc.status === 'running');
  if (running.length > 0) {
    const label = TOOL_STATUS_LABELS[running[running.length - 1].toolName];
    return label ? `${label}...` : 'Working...';
  }
  return 'Thinking';
}

interface ChatThreadProps {
  messages: ChatMessage[];
  isLoading: boolean;
  variant?: 'compact' | 'full';
}

const ContextStatusBadge: React.FC<{
  metadata: { totalRequested: number; totalLoaded: number; failures: Array<{ type: string; id: string; reason: string }> };
}> = ({ metadata }) => {
  if (metadata.totalRequested === 0) return null;

  const allLoaded = metadata.totalLoaded === metadata.totalRequested;
  const noneLoaded = metadata.totalLoaded === 0;

  return (
    <div className={`flex items-center gap-1.5 px-2 py-1 mb-1.5 rounded text-[11px] font-medium ${
      noneLoaded
        ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400'
        : allLoaded
          ? 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400'
          : 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400'
    }`}>
      {allLoaded ? (
        <Check className="h-3 w-3 flex-shrink-0" />
      ) : (
        <AlertTriangle className="h-3 w-3 flex-shrink-0" />
      )}
      <span>{metadata.totalLoaded}/{metadata.totalRequested} sources loaded</span>
      {!allLoaded && metadata.failures.length > 0 && (
        <span className="text-[10px] opacity-75 ml-1">
          ({[...new Set(metadata.failures.map(f => f.reason))].join(', ')})
        </span>
      )}
    </div>
  );
};

export const ChatThread: React.FC<ChatThreadProps> = ({ messages, isLoading, variant = 'compact' }) => {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  if (messages.length === 0 && !isLoading) {
    return null;
  }

  const isFull = variant === 'full';
  const textSize = isFull ? '[&>*]:text-sm' : '[&>*]:text-xs';
  const maxWidth = isFull ? 'max-w-[85%]' : 'max-w-[90%]';

  // Don't show the bottom loading indicator when the last assistant message
  // already has its own thinking/tool-call/streaming state
  const lastMsg = messages[messages.length - 1];
  const lastAssistantIsActive = lastMsg?.role === 'assistant' &&
    (lastMsg.isThinking || lastMsg.isStreaming || (lastMsg.toolCalls && lastMsg.toolCalls.length > 0));

  return (
    <div className={`space-y-3 ${isFull ? 'space-y-4' : ''}`}>
      {messages.map((message) => {
        // Is this assistant message still actively working (thinking, running tools, or streaming)?
        const hasRunningTools = message.toolCalls?.some(tc => tc.status === 'running');
        const isWorking = message.role === 'assistant' &&
          (message.isThinking || hasRunningTools) &&
          !message.content;
        const isStillProcessing = message.role === 'assistant' &&
          (message.isStreaming || message.isThinking || hasRunningTools);

        return (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start gap-2'}`}
          >
            {message.role === 'assistant' && (
              <div className="w-5 h-5 mt-1 rounded-md bg-primary-600 dark:bg-primary-700 flex items-center justify-center flex-shrink-0 shadow-sm">
                <Sparkles className="h-3 w-3 text-white" />
              </div>
            )}
            <div
              className={`${maxWidth} rounded-lg px-3 py-2 ${isFull ? 'px-4 py-3' : ''} ${
                message.role === 'user'
                  ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-900 dark:text-primary-100 shadow-sm border border-primary-200 dark:border-primary-800/50'
                  : 'bg-medical-50/80 dark:bg-medical-700/80 text-medical-700 dark:text-medical-200 shadow-sm shadow-medical-200/50 dark:shadow-none border border-medical-100 dark:border-medical-600/50'
              } ${message.role === 'assistant' && !message.isStreaming ? 'animate-[fadeSlideIn_0.3s_ease-out]' : ''}`}
            >
              {message.role === 'assistant' && message.contextMetadata && (
                <ContextStatusBadge metadata={message.contextMetadata} />
              )}

              {/* Thinking indicator — animated dots with contextual status */}
              {isWorking && (
                <div className="py-1.5 mb-1">
                  <ThinkingIndicator statusText={getThinkingStatus(message.toolCalls)} />
                </div>
              )}

              {/* Tool call indicators (collapsed by default) */}
              {message.role === 'assistant' && message.toolCalls && message.toolCalls.length > 0 && (
                <AgentToolCallIndicator toolCalls={message.toolCalls} variant={variant} />
              )}

              {message.role === 'assistant' && message.content ? (
                <div className={`prose prose-sm max-w-none dark:prose-invert ${textSize} [&>*]:leading-relaxed [&>*]:font-normal [&_strong]:font-medium [&_hr]:hidden ${message.isStreaming ? 'streaming-cursor' : ''}`}>
                  <MarkdownWithCitations
                    content={message.content}
                    citations={message.sources?.map((s: AskSource, i: number) =>
                      message.isStreaming
                        ? fromAskSource(s)
                        : fromAskSourceWithExcerpt(s, message.content, i + 1)
                    )}
                  />
                </div>
              ) : message.role === 'user' ? (
                <p className={`${isFull ? 'text-sm' : 'text-[13px]'} font-normal whitespace-pre-wrap`}>{message.content}</p>
              ) : null}

              {/* Output widgets — rendered inline for completed tool calls with structured data */}
              {message.role === 'assistant' && message.toolCalls && message.toolCalls.length > 0 && (() => {
                const widgets = message.toolCalls
                  .filter(tc => tc.status === 'done' && tc.widgetData?.outputWidget)
                  .map(tc => {
                    const outputData = tc.widgetData!.outputWidget as { type: string; [key: string]: unknown };
                    const Widget = OUTPUT_WIDGET_REGISTRY[outputData.type];
                    if (!Widget) return null;
                    return (
                      <OutputWidgetBlock key={tc.toolUseId + '-output'}>
                        <Widget data={outputData} />
                      </OutputWidgetBlock>
                    );
                  })
                  .filter(Boolean);
                return widgets.length > 0 ? <div className="space-y-2 my-2">{widgets}</div> : null;
              })()}

              {/* Bottom processing indicator — always visible while still working */}
              {isStillProcessing && !isWorking && (
                <div className="pt-1.5 mt-1.5">
                  <ThinkingIndicator size="sm" statusText={getThinkingStatus(message.toolCalls)} />
                </div>
              )}

              {!isWorking && (
                <p className={`text-[10px] mt-1 ${
                  message.role === 'user' ? 'opacity-70' : 'text-medical-400 dark:text-medical-500'
                }`}>
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
          </div>
        );
      })}

      {isLoading && !lastAssistantIsActive && (
        <div className="flex justify-start gap-2 animate-[fadeSlideIn_0.3s_ease-out]">
          <div className="w-5 h-5 mt-1 rounded-md bg-primary-600 dark:bg-primary-700 flex items-center justify-center flex-shrink-0 shadow-sm">
            <Sparkles className="h-3 w-3 text-white" />
          </div>
          <div className="bg-white dark:bg-medical-700/80 shadow-sm shadow-medical-200/50 dark:shadow-none border border-medical-100 dark:border-medical-600/50 rounded-lg px-3 py-2.5">
            <ThinkingIndicator statusText="Thinking" />
          </div>
        </div>
      )}

      <div ref={endRef} />

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes blink {
          50% { opacity: 0; }
        }
        .streaming-cursor > *:last-child::after {
          content: '\\25CB';
          display: inline-block;
          margin-left: 2px;
          color: #1b93a3;
          animation: blink 0.8s step-end infinite;
        }
      `}</style>
    </div>
  );
};
